import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Search, X } from "lucide-react";
import { Dialog, Input } from "@akuna/ui";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/utils/string-utils";
import { toast } from "@/hooks/use-toast";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useAddStayPackMember, useRemoveStayPackMember, type StayPack } from "@/hooks/useStayPacks";
import { useStayPackPetSearch } from "@/hooks/useStayPackPetSearch";
import { colorDotClass, colorDotStyle } from "./colors";

/**
 * The full standing roster for a pack — every pet ever added, not just the ones
 * with a reservation today.
 */
export default function PackMembersDialog({
  open,
  onOpenChange,
  pack,
  canEdit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pack: StayPack | null;
  canEdit: boolean;
}) {
  const { t } = useTranslation("daycareHotel");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const addMember = useAddStayPackMember();
  const removeMember = useRemoveStayPackMember();
  const { data: petOptions, isLoading: isSearching } = useStayPackPetSearch(debouncedSearch, open);

  const memberPetIds = useMemo(
    () => new Set((pack?.members ?? []).map((member) => member.petId)),
    [pack],
  );
  const candidates = useMemo(
    () => (petOptions ?? []).filter((option) => !memberPetIds.has(option.petId)),
    [petOptions, memberPetIds],
  );

  if (!pack) return null;

  const handleAdd = async (petId: string) => {
    try {
      await addMember.mutateAsync({ packId: pack.id, petId });
      toast({ title: t("packs.toastMemberAdded") });
    } catch {
      toast({ variant: "destructive", title: t("packs.toastMemberFailed") });
    }
  };

  const handleRemove = async (memberId: string) => {
    try {
      await removeMember.mutateAsync(memberId);
      toast({ title: t("packs.toastMemberRemoved") });
    } catch {
      toast({ variant: "destructive", title: t("packs.toastMemberFailed") });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("packs.membersDialogTitle", { name: pack.name })}
      description={t("packs.membersDialogDescription")}
    >
      <div className="flex flex-col gap-5">
        <section>
          <h3 className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-color-light">
            {t("packs.membersCurrent", { count: pack.members.length })}
          </h3>
          {pack.members.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border-gray px-4 py-6 text-center text-[13px] text-muted-foreground">
              {t("packs.membersEmpty")}
            </p>
          ) : (
            <ul className="flex max-h-64 flex-col gap-1.5 overflow-y-auto">
              {pack.members.map((member) => (
                <li
                  key={member.id}
                  className="flex items-center gap-2.5 rounded-xl border border-border-gray px-3 py-2"
                >
                  <span
                    className={cn(
                      "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-primary-foreground",
                      colorDotClass(pack.color),
                    )}
                    style={colorDotStyle(pack.color)}
                    aria-hidden="true"
                  >
                    {initials(member.petName)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13.5px] font-semibold text-foreground">
                      {member.petName}
                    </span>
                    {member.ownerName ? (
                      <span className="block truncate text-xs text-muted-foreground">{member.ownerName}</span>
                    ) : null}
                  </span>
                  {canEdit ? (
                    <button
                      type="button"
                      onClick={() => handleRemove(member.id)}
                      disabled={removeMember.isPending}
                      aria-label={t("packs.removeMember", { name: member.petName })}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-color-light transition-colors hover:bg-state-danger-soft hover:text-state-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        {canEdit ? (
          <section>
            <h3 className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-color-light">
              {t("packs.membersAdd")}
            </h3>
            <div className="relative mb-2.5">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-color-light"
                aria-hidden="true"
              />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("packs.membersSearchPlaceholder")}
                aria-label={t("packs.membersSearchPlaceholder")}
                className="pl-9"
              />
            </div>
            {isSearching ? (
              <p className="py-4 text-center text-[13px] text-muted-foreground">{t("common.loading")}</p>
            ) : candidates.length === 0 ? (
              <p className="py-4 text-center text-[13px] text-muted-foreground">
                {t("packs.membersNoResults")}
              </p>
            ) : (
              <ul className="flex max-h-52 flex-col gap-1.5 overflow-y-auto">
                {candidates.map((option) => (
                  <li key={option.petId}>
                    <button
                      type="button"
                      onClick={() => handleAdd(option.petId)}
                      disabled={addMember.isPending}
                      className="flex w-full items-center gap-2.5 rounded-xl border border-border-gray px-3 py-2 text-left transition-colors hover:bg-background-app focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13.5px] font-semibold text-foreground">
                          {option.petName}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {option.ownerName}
                        </span>
                      </span>
                      <Plus className="h-4 w-4 shrink-0 text-color-light" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}
      </div>
    </Dialog>
  );
}
